/**
 * Sort the two product slugs alphabetically and join with "_" so analytics
 * can group pair-level data regardless of which slug came first in the URL.
 */
export function getComparePairId(slugA: string, slugB: string): string {
  return [slugA, slugB].sort().join('_')
}

/**
 * Extract the category slug from a breadcrumb URL like
 *   "/en-ie/l/smartphones/abc-uuid-1234"
 * → "smartphones"
 *
 * Returns undefined when the URL doesn't match the expected `/l/<slug>` shape.
 */
export function getCategorySlug(url: string | undefined): string | undefined {
  if (!url) return undefined
  const match = url.match(/\/l\/([^/]+)/)

  return match?.[1]
}

/**
 * Maps the internal anchor / scoreId used by `comparisonHelper.ts` (and by the
 * RelatedContent component for carousel sections) to the canonical `section_name`
 * expected by the tracking plan, alongside its DOM level and whether the section
 * carries LLM-generated content.
 *
 * Note: `sound_quality`, `multimedia` and `camera` extend the plan's enum (which
 * grouped them under `audiovisual`) so that we keep the granularity that already
 * exists in the helper.
 */
export const SECTION_TRACKING_MAP = {
  introduction: {
    name: 'differences_at_a_glance',
    level: 'h2',
    hasLlmText: true,
  },
  scores: { name: 'scores', level: 'h2', hasLlmText: false },
  life_expectancy_score: {
    name: 'durability',
    level: 'h3',
    hasLlmText: true,
  },
  performances_score: { name: 'performance', level: 'h3', hasLlmText: true },
  screen_quality_score: {
    name: 'screen_quality',
    level: 'h3',
    hasLlmText: true,
  },
  sound_quality_score: {
    name: 'sound_quality',
    level: 'h3',
    hasLlmText: true,
  },
  multimedia_score: { name: 'multimedia', level: 'h3', hasLlmText: true },
  camera_score: { name: 'camera', level: 'h3', hasLlmText: true },
  miscellaneous: { name: 'miscellaneous', level: 'h3', hasLlmText: true },
  conclusion: {
    name: 'what_the_community_thinks',
    level: 'h2',
    hasLlmText: true,
  },
  related_articles: {
    name: 'see_all_articles',
    level: 'h2',
    hasLlmText: false,
  },
  related_comparisons: {
    name: 'our_recent_comparisons',
    level: 'h2',
    hasLlmText: false,
  },
} as const satisfies Record<
  string,
  { name: string; level: 'h2' | 'h3'; hasLlmText: boolean }
>

export type ComparisonSectionKey = keyof typeof SECTION_TRACKING_MAP
